import React, { useState, useRef } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  InputBase,
} from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  Verified as VerifiedIcon,
  EmojiEvents as TrophyIcon,
  Bolt as BoltIcon,
  AccountBalanceWalletOutlined as WalletIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";
import { Cherry, Gem } from "lucide-react";
import { TbNumber7 } from "react-icons/tb";

const SYMBOLS = [
  {
    id: "cherry",
    symbol: <Cherry size="1em" strokeWidth={2} style={{ transform: "translateY(-25%)" }} />,
    label: "Cherry",
    payout: "Small Win",
  },
  {
    id: "bar",
    symbol: "BAR",
    label: "BAR",
    payout: "Medium Win",
  },
  {
    id: "diamond",
    symbol: <Gem size="1em" strokeWidth={2} />,
    label: "Diamond",
    payout: "Major Win",
  },
  {
    id: "seven",
    symbol: <TbNumber7 size="1.2em" strokeWidth={2} />,
    label: "Seven",
    payout: "JACKPOT",
  },
];

const getRandomSymbol = () => {
  return SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
};

const SlotMachineCard = ({ lotteryData, account, onConnectWallet }) => {
  const [reels, setReels] = useState([
    SYMBOLS[0],
    SYMBOLS[1],
    SYMBOLS[3],
  ]);


  const [isSpinning, setIsSpinning] = useState(false);
  const spinningReelsRef = useRef([false, false, false]);
  const [result, setResult] = useState(null);

  const handleSpin = () => {
    if (!account) {
      if (onConnectWallet) onConnectWallet();
      return;
    }

    if (isSpinning) return;

    setIsSpinning(true);
    spinningReelsRef.current = [true, true, true];
    setResult(null);

    const finalReels = [
      getRandomSymbol(),
      getRandomSymbol(),
      getRandomSymbol(),
    ];

    const interval = setInterval(() => {
      setReels(prev => prev.map((r, i) => 
        spinningReelsRef.current[i] ? getRandomSymbol() : r
      ));
    }, 100);

    setTimeout(() => {
      spinningReelsRef.current[0] = false;
      setReels(prev => [finalReels[0], prev[1], prev[2]]);
    }, 800);

    setTimeout(() => {
      spinningReelsRef.current[1] = false;
      setReels(prev => [finalReels[0], finalReels[1], prev[2]]);
    }, 1400);

    setTimeout(() => {
      spinningReelsRef.current[2] = false;
      setReels(finalReels);
      clearInterval(interval);
      setIsSpinning(false);

      const isMatch =
        finalReels[0].id === finalReels[1].id &&
        finalReels[1].id === finalReels[2].id;

      if (isMatch) {
        setResult({
          type:
            finalReels[0].id === "seven"
              ? "jackpot"
              : "win",
          symbol: finalReels[0],
        });
      } else {
        setResult({
          type: "lose",
          symbol: null,
        });
      }
    }, 2000);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/slotlottery`;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <Box
      className={`slot-machine-card ${
        isSpinning ? "is-spinning" : ""
      }`}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          pb: 1.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: colors.primary.lighter,
                color: colors.primary.main,
                fontWeight: 700,
              }}
            >
              A
            </Avatar>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                  }}
                >
                  {lotteryData.creator}
                </Typography>

                <VerifiedIcon
                  sx={{
                    fontSize: 14,
                    color: colors.primary.main,
                  }}
                />
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontFamily: "monospace",
                }}
              >
                Machine #{lotteryData.lotteryId}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
          >
            <CommonChip
              label={`${lotteryData.jackpotLevel} Jackpot`}
              variant="neutral"
              size="small"
            />

            <Tooltip title="Share Slot Machine" arrow>
              <IconButton
                size="small"
                onClick={handleShare}
                sx={{
                  color: colors.slate[400],
                  p: 0.5,
                }}
              >
                <ShareIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Jackpot / Bet */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: colors.slate[50],
          borderTop: `1px solid ${colors.slate[100]}`,
          borderBottom: `1px solid ${colors.slate[100]}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Progressive Jackpot
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: colors.emerald[600],
                lineHeight: 1.2,
              }}
            >
              {lotteryData.jackpot}{" "}
              <span style={{ fontSize: "0.8rem" }}>
                {lotteryData.currency}
              </span>
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              ENTRY TICKET
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: colors.slate[800],
                lineHeight: 1.2,
              }}
            >
              {lotteryData.betAmount}{" "}
              <span style={{ fontSize: "0.8rem" }}>
                {lotteryData.currency}
              </span>
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Slot Machine */}
      <Box className="slot-machine-stage">
        {/* Machine Top */}
        <Box className="slot-machine-top">
          <Typography
            variant="caption"
            className="slot-machine-label"
          >
            3-SLOTBET
          </Typography>

          <Typography
            variant="caption"
            className="slot-machine-live"
          >
            ● LIVE
          </Typography>
        </Box>

        {/* Reels */}
        <Box className="slot-reels-wrapper">
          <Box className="slot-payline" />

          {reels.map((reel, index) => {
            const isReelSpinning = spinningReelsRef.current[index];
            const symIndex = SYMBOLS.findIndex(s => s.id === reel.id);
            const topSym = isReelSpinning ? getRandomSymbol().symbol : SYMBOLS[(symIndex + 1) % SYMBOLS.length].symbol;
            const bottomSym = isReelSpinning ? getRandomSymbol().symbol : SYMBOLS[(symIndex + SYMBOLS.length - 1) % SYMBOLS.length].symbol;

            return (
              <Box
                key={`${reel.id}-${index}`}
                className={`slot-reel ${
                  isReelSpinning ? "reel-spinning" : ""
                }`}
              >
                <Typography
                  className="slot-symbol slot-symbol-top"
                  sx={{ fontSize: topSym === "BAR" ? "1.1rem" : undefined }}
                >
                  {topSym}
                </Typography>

                <Typography
                  className="slot-symbol slot-symbol-main"
                  sx={{ fontSize: reel.symbol === "BAR" ? "1.3rem" : undefined, fontWeight: reel.symbol === "BAR" ? 900 : undefined }}
                >
                  {reel.symbol}
                </Typography>

                <Typography
                  className="slot-symbol slot-symbol-bottom"
                  sx={{ fontSize: bottomSym === "BAR" ? "1.1rem" : undefined }}
                >
                  {bottomSym}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Payline indicator */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.25 }}
        >
          <Box className="payline-dot" />

          <Typography
            variant="caption"
            sx={{
              color: colors.slate[500],
              fontWeight: 600,
            }}
          >
            Match all 3 symbols on the center line
          </Typography>

          <Box className="payline-dot" />
        </Stack>
      </Box>

      {/* Result */}
      <Box sx={{ px: 2.5 }}>
        {result && (
          <Box
            className={`slot-result ${
              result.type === "jackpot"
                ? "slot-result-jackpot"
                : result.type === "win"
                ? "slot-result-win"
                : "slot-result-loss"
            }`}
          >
            {result.type === "lose" ? (
              <BoltIcon
                sx={{
                  fontSize: 22,
                  color: colors.slate[400],
                }}
              />
            ) : (
              <TrophyIcon
                sx={{
                  fontSize: 22,
                  color:
                    result.type === "jackpot"
                      ? colors.amber[500]
                      : colors.emerald[600],
                }}
              />
            )}

              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color:
                      result.type === "lose"
                        ? colors.slate[700]
                        : result.type === "jackpot"
                        ? colors.amber[800]
                        : colors.emerald[800],
                  }}
                >
                  {result.type === "jackpot"
                    ? "JACKPOT!"
                    : result.type === "win"
                    ? `${result.symbol.label} MATCH!`
                    : "Better Luck Next Spin"}
                </Typography>
              </Box>

              <CommonChip
                label={
                  result.type === "lose"
                    ? "MISSED"
                    : result.type === "jackpot"
                    ? "JACKPOT"
                    : "WIN"
                }
                variant={
                  result.type === "lose"
                    ? "neutral"
                    : "active"
                }
                size="small"
                sx={{ fontWeight: 800 }}
              />
          </Box>
        )}
      </Box>

      {/* Action */}
      <Box
        sx={{
          p: 2.5,
          pt: result ? 1.5 : 2.5,
        }}
      >
        <CommonButton
          variant="primary"
          fullWidth
          startIcon={!account ? <WalletIcon /> : <BoltIcon />}
          onClick={handleSpin}
          disabled={isSpinning}
          sx={{
            py: 1.25,
            fontWeight: 800,
            fontSize: "0.95rem",
          }}
        >
          {isSpinning ? "SPINNING..." : !account ? "CONNECT WALLET" : `${lotteryData.betAmount} ${lotteryData.currency} TO SPIN`}
        </CommonButton>
      </Box>
    </Box>
  );
};

export default SlotMachineCard;